import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import { OptionItemSquareList } from '@/pages/Onboarding/components/OptionItemSquare/OptionItemSquare';
import { OptionItemSquareLight } from '@/pages/Onboarding/components/OptionItemSquareLight';
import { useOnboarding } from '@/pages/Onboarding/context';
import { HAIR_COLOR_OPTIONS } from '@/pages/Onboarding/options/HairColor/HAIR_COLOR_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { LayoutDataType } from '../../types';

import { ScreenHairColorStyles } from './HairColor.styles';

export const ScreenHairColor: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'HairColor',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const hairColor = form.watch('hair_color');

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['hair_color']>,
  ) => {
    form.setValue('hair_color', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        hair_color: option.title,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(wizard.nextStep);
      }, 200);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  const handleBackStep = (): void => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('body_shape'));
    } else {
      wizard.goToStep('body_shape');
    }
  };

  return (
    <ScreenHairColorStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />

      <OwnAiTitle animate={shouldAnimate} show={showElements}>
        Select her <OwnAiTitleHighlight>hair color</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSquareList>
          {HAIR_COLOR_OPTIONS.map((option) => (
            <OptionItemSquareLight
              key={option.id}
              isSelected={hairColor === option.id}
              onClick={() => handleSelect(option)}
              bgSrc={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/options/hair_color/${option.id}.png`}
              title={option.title}
              TEST_ID={`hair_color-${option.id}`}
              animate={shouldAnimate}
              show={showElements}
            />
          ))}
        </OptionItemSquareList>
      </IonContent>
    </ScreenHairColorStyles>
  );
};
